import React, { useState, useEffect, useRef } from "react";
import {
  CreditCard,
  Train,
  Bus,
  Smartphone,
  AlertCircle,
  HelpCircle,
  RefreshCw,
  CheckCircle,
  XCircle,
  MessageSquare,
  Ticket,
  Phone,
  Building2,
  Send,
  Pencil,
  Languages,
  ChevronDown,
  Sun,
  Moon,
} from "lucide-react";
import { api, defaults, prompts } from "./config";
import scrapedFares from "./fares.json";
import { translateToFrench, warmUpTranslator } from "./utils/translate";
import { Markdown } from "./utils/markdown";

// --- Component: TabButton ---
// Reusable component for navigation tabs
// Tab <-> URL hash mapping so tabs are shareable and the browser Back button works.
const TAB_HASHES = {
  paymentMethods: "payment",
  emergencyHelp: "emergency",
  troubleshooting: "troubleshooting",
  transitAssistant: "assistant",
};
const tabForHash = (hash) => {
  const h = (hash || "").replace(/^#/, "");
  return (
    Object.keys(TAB_HASHES).find((k) => TAB_HASHES[k] === h) || "paymentMethods"
  );
};

const TabButton = ({ icon, label, isActive, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={isActive}
    title={label}
    aria-label={label}
    className={`flex-1 flex items-center justify-center px-2 sm:px-4 py-3 text-sm font-medium rounded-md transition-all duration-300 ease-in-out
      ${
        isActive
          ? "bg-blue-800 text-white shadow-lg" // Darker French Blue for active tab
          : "bg-transparent text-gray-700 hover:bg-blue-50 hover:text-blue-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-blue-300" // Lighter hover, French Blue text
      }`}
    onClick={onClick}
  >
    {icon}
    {/* Label hides on small screens so four tabs fit; icon + aria-label remain */}
    <span className="hidden sm:inline sm:ml-2">{label}</span>
  </button>
);

// --- Component: Card ---
// Reusable component for content sections with a title and icon
const Card = ({ title, icon, children }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-5 mb-4 border border-gray-200 dark:border-gray-700 shadow-sm">
    <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 flex items-center mb-3">
      {icon && <span className="mr-2">{icon}</span>}
      {title}
    </h3>
    <div className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">{children}</div>
  </div>
);

// --- Component: PaymentMethodsTab ---
// Content for the Payment Methods tab - Updated for 2026 fare reform
const PaymentMethodsTab = ({ pricingData }) => (
  <div>
    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Tickets & Fares (2026)</h2>

    {/* Quick comparison: at-a-glance decision helper */}
    <div className="mb-6 rounded-lg border border-blue-200 dark:border-gray-700 bg-blue-50/60 dark:bg-gray-900/40 p-4">
      <h3 className="text-base font-bold text-blue-900 dark:text-blue-300 mb-3">Which one should I get?</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead>
            <tr className="text-gray-600 dark:text-gray-400 border-b border-blue-200 dark:border-gray-700">
              <th className="py-2 pr-3 font-semibold">Option</th>
              <th className="py-2 px-3 font-semibold whitespace-nowrap">Cost</th>
              <th className="py-2 pl-3 font-semibold">Best for</th>
            </tr>
          </thead>
          <tbody className="text-gray-700 dark:text-gray-300">
            <tr className="border-b border-blue-100 dark:border-gray-700">
              <td className="py-2 pr-3 font-medium">Contactless bank card / phone</td>
              <td className="py-2 px-3 whitespace-nowrap text-blue-800 dark:text-blue-300 font-semibold">{pricingData.singleTicket}/trip</td>
              <td className="py-2 pl-3">Easiest: nothing to buy, just tap</td>
            </tr>
            <tr className="border-b border-blue-100 dark:border-gray-700">
              <td className="py-2 pr-3 font-medium">Navigo Easy + single tickets</td>
              <td className="py-2 px-3 whitespace-nowrap text-blue-800 dark:text-blue-300 font-semibold">{pricingData.navigoEasyCard} card</td>
              <td className="py-2 pl-3">A few occasional trips</td>
            </tr>
            <tr className="border-b border-blue-100 dark:border-gray-700">
              <td className="py-2 pr-3 font-medium">Navigo Liberté+</td>
              <td className="py-2 px-3 whitespace-nowrap text-blue-800 dark:text-blue-300 font-semibold">{pricingData.navigoLibertePlus}/trip · {pricingData.dailyCap} cap</td>
              <td className="py-2 pl-3">Lowest per-trip for regular use</td>
            </tr>
            <tr>
              <td className="py-2 pr-3 font-medium">Navigo Découverte (weekly)</td>
              <td className="py-2 px-3 whitespace-nowrap text-blue-800 dark:text-blue-300 font-semibold">~{pricingData.weeklyPass} + {pricingData.navigoDecouverte} card</td>
              <td className="py-2 pl-3">Staying 5+ days with heavy use</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Details for each option below.</p>
    </div>

    {/* Metro-Train-RER Ticket */}
    <Card
      title="Metro-Train-RER Ticket"
      icon={<Train className="w-6 h-6 text-blue-700" />}
    >
      <p className="mb-2">
        Single ticket valid for metro, RER, Transilien trains, and funicular within
        the Île-de-France region. Flat fare regardless of distance.
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Full Price:</span>{" "}
          <span className="text-blue-700 dark:text-blue-300 font-bold">{pricingData.singleTicket}</span>
        </li>
        {pricingData.singleTicketReduced && (
          <li>
            <span className="font-semibold">Reduced Price:</span>{" "}
            <span className="text-green-600 dark:text-green-400 font-bold">{pricingData.singleTicketReduced}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400"> (children 4-10, disability card holders)</span>
          </li>
        )}
        <li>
          <span className="font-semibold">Valid for:</span> 2 hours, unlimited transfers within the network
        </li>
      </ul>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Load onto a Navigo Easy card, smartphone app, or smartwatch.
      </p>
    </Card>

    {/* Bus-Tram Ticket */}
    <Card
      title="Bus-Tram Ticket"
      icon={<Bus className="w-6 h-6 text-green-600" />}
    >
      <p className="mb-2">
        Single ticket for buses and trams. Different from metro tickets since the 2025 fare reform.
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Full Price:</span>{" "}
          <span className="text-blue-700 dark:text-blue-300 font-bold">{pricingData.busTramTicket}</span>
        </li>
        {pricingData.busTramTicketReduced && (
          <li>
            <span className="font-semibold">Reduced Price:</span>{" "}
            <span className="text-green-600 dark:text-green-400 font-bold">{pricingData.busTramTicketReduced}</span>
          </li>
        )}
        <li>
          <span className="font-semibold">Valid for:</span> 1h30 with unlimited bus/tram transfers
        </li>
      </ul>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">Note:</span> Cannot be used on metro/RER. Buy the Metro-Train-RER ticket for those.
      </p>
    </Card>

    {/* Navigo Easy Card */}
    <Card
      title="Navigo Easy Card"
      icon={<CreditCard className="w-6 h-6 text-blue-700" />}
    >
      <p className="mb-2">
        A rechargeable contactless card for occasional travelers. Load tickets onto it
        and tap to travel.
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Card Cost:</span>{" "}
          <span className="text-blue-700 dark:text-blue-300 font-bold">{pricingData.navigoEasyCard}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400"> (one-time, reusable)</span>
        </li>
        <li>Load Metro-Train-RER or Bus-Tram tickets as needed</li>
        <li>Card is anonymous and can be shared</li>
      </ul>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">Where to buy:</span> Ticket machines at any metro, RER, or tram station.
      </p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">Best for:</span> Tourists making occasional trips.
      </p>
    </Card>

    {/* Navigo Liberté+ */}
    <Card
      title="Navigo Liberté+ (Pay-as-you-go)"
      icon={<Smartphone className="w-6 h-6 text-purple-600" />}
    >
      <p className="mb-2">
        Link your bank card and get charged automatically at the best rate. No need to buy tickets in advance.
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Metro/Train/RER:</span>{" "}
          <span className="text-blue-700 dark:text-blue-300 font-bold">{pricingData.navigoLibertePlus}</span> per trip
        </li>
        <li>
          <span className="font-semibold">Bus/Tram:</span>{" "}
          <span className="text-blue-700 dark:text-blue-300 font-bold">{pricingData.navigoLiberteBus}</span> per trip
        </li>
        <li>
          <span className="font-semibold">Daily Cap:</span>{" "}
          <span className="text-green-600 dark:text-green-400 font-bold">{pricingData.dailyCap} max</span>
          <span className="text-sm text-gray-500 dark:text-gray-400"> (excl. airport trips)</span>
        </li>
      </ul>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">How to set up:</span> Register at iledefrance-mobilites.fr or via the app.
      </p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">Best for:</span> Regular travelers who want the lowest per-trip cost.
      </p>
    </Card>

    {/* Contactless Bank Card */}
    <Card
      title="Contactless Bank Card / Mobile Pay"
      icon={<CreditCard className="w-6 h-6 text-blue-700" />}
    >
      <p className="mb-2">
        Tap your contactless Visa/Mastercard or smartphone (Apple Pay, Google Pay) directly at gates and validators.
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>No registration or ticket purchase needed</li>
        <li>Charged the standard single ticket fare per journey</li>
        <li>
          <span className="font-semibold">Important:</span> Use the SAME card/device for tap-in AND tap-out
        </li>
      </ul>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">Tip:</span> Works on metro, RER, buses, and trams throughout Paris.
      </p>
    </Card>

    {/* Navigo Découverte / Passes */}
    <Card
      title="Navigo Découverte (Weekly/Monthly Pass)"
      icon={<Ticket className="w-6 h-6 text-red-600" />}
    >
      <p className="mb-2">
        Unlimited travel pass for frequent travelers. Requires a passport photo.
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Card Cost:</span>{" "}
          <span className="text-blue-700 dark:text-blue-300 font-bold">{pricingData.navigoDecouverte}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400"> (one-time)</span>
        </li>
        <li>
          <span className="font-semibold">Weekly Pass (zones 1-5):</span> ~{pricingData.weeklyPass}
          <span className="text-sm text-gray-500 dark:text-gray-400"> (Mon-Sun only)</span>
        </li>
        <li>
          <span className="font-semibold">Monthly Pass (zones 1-5):</span> ~{pricingData.monthlyPass}
        </li>
      </ul>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">Where to buy:</span> Metro/RER station counters (bring a photo).
      </p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">Best for:</span> Staying 5+ days with heavy transit use.
      </p>
    </Card>

    {/* Official Apps */}
    <Card
      title="Official Apps (Plan & Buy on Your Phone)"
      icon={<Smartphone className="w-6 h-6 text-blue-700" />}
    >
      <p className="mb-3">
        Two free official apps. Download them on Wi-Fi before you travel — setup is
        easier before you're underground.
      </p>
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-gray-800 dark:text-gray-100">
            Île-de-France Mobilités{" "}
            <span className="font-normal text-sm text-gray-500 dark:text-gray-400">
              — buy &amp; load Navigo tickets
            </span>
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <a
              href={api.apps.ileDeFranceMobilites.ios}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-lg border border-blue-200 dark:border-gray-600 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 transition hover:bg-blue-50 dark:hover:bg-gray-700"
            >
               App Store
            </a>
            <a
              href={api.apps.ileDeFranceMobilites.android}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-lg border border-blue-200 dark:border-gray-600 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 transition hover:bg-blue-50 dark:hover:bg-gray-700"
            >
              ▶ Google Play
            </a>
          </div>
        </div>
        <div>
          <p className="font-semibold text-gray-800 dark:text-gray-100">
            Bonjour RATP{" "}
            <span className="font-normal text-sm text-gray-500 dark:text-gray-400">
              — routes, live times &amp; maps
            </span>
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <a
              href={api.apps.bonjourRatp.ios}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-lg border border-blue-200 dark:border-gray-600 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 transition hover:bg-blue-50 dark:hover:bg-gray-700"
            >
               App Store
            </a>
            <a
              href={api.apps.bonjourRatp.android}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-lg border border-blue-200 dark:border-gray-600 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 transition hover:bg-blue-50 dark:hover:bg-gray-700"
            >
              ▶ Google Play
            </a>
          </div>
        </div>
      </div>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold">Heads up:</span> foreign cards can be finicky in
        the ticketing app. If it won't accept your card, just tap a contactless bank card
        directly at the gate instead.
      </p>
    </Card>
  </div>
);

// --- Component: EmergencyHelpTab ---
// Content for the Emergency Help tab
const EmergencyHelpTab = () => (
  <div>
    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
      Emergency Help (Do Not Panic){" "}
    </h2>
    <Card
      title="Emergency Numbers — Save These Now"
      icon={<Phone className="w-6 h-6 text-red-700" />}
    >
      <p className="mb-2">
        These work from any phone in France, including a locked phone with no
        SIM. You do not need credit or a French number.
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">112</span> — EU-wide emergency
          (police, medical, or fire). English-speaking operators are available.
          When in doubt, call this one.
        </li>
        <li>
          <span className="font-semibold">3117</span> — Official safety line for
          the Paris transit network (metro, RER, tram, bus). Use it if you feel
          unsafe, are harassed, or are a victim of theft on the network. Text{" "}
          <span className="font-semibold">31177</span> if you can't safely
          speak.
        </li>
        <li>
          <span className="font-semibold">17</span> — Police ·{" "}
          <span className="font-semibold">15</span> — Medical (SAMU) ·{" "}
          <span className="font-semibold">18</span> — Fire brigade
        </li>
        <li>
          <span className="font-semibold">114</span> — Emergency by SMS, for the
          hearing-impaired or any time you cannot speak out loud.
        </li>
      </ul>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Save 112 and 3117 in your phone before you travel.
      </p>
    </Card>
    <Card
      title="Already Traveling Without a Valid Ticket?"
      icon={<AlertCircle className="w-6 h-6 text-red-700" />}
    >
      {" "}
      {/* Darker French Red icon */}
      <p className="mb-2">
        If you genuinely tried to pay but couldn't, here's what to do:
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Do NOT try to evade:</span> If
          approached by a ticket inspector, explain your situation calmly and
          honestly.
        </li>
        <li>
          <span className="font-semibold">Explain the issue:</span> State that
          your payment method was rejected despite your attempts to pay.
        </li>
        <li>
          <span className="font-semibold">Offer to pay the fare:</span> Ask if
          you can pay for a ticket on the spot or at the next station.
        </li>
        <li>
          <span className="font-semibold">Fines:</span> Be aware that inspectors
          may still issue a fine, especially if they deem your explanation
          insufficient. Fines are much higher than the ticket price.
        </li>
      </ul>
    </Card>
    <Card
      title="Stranded or Need Help in English?"
      icon={<AlertCircle className="w-6 h-6 text-red-700" />}
    >
      <p className="mb-2">
        If you're lost, stuck, or overwhelmed and the emergency lines above are
        too much, these are gentler, English-friendly options:
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Station agents (Accueil):</span> Look
          for "Information" or "Accueil" counters at larger metro/RER stations.
          Human agents can often help — and process payments — when machines
          can't.
        </li>
        <li>
          <span className="font-semibold">SOS Voyageurs:</span> Volunteers who
          help stranded travelers at major Paris stations (Gare du Nord, Gare de
          Lyon, Montparnasse, Austerlitz, Saint-Lazare, Est).
        </li>
        <li>
          <span className="font-semibold">SOS Help:</span> English-language
          listening and crisis line in France —{" "}
          <span className="font-semibold">01 46 21 46 46</span> (daily,
          afternoon to late evening). Good if you're distressed and want to talk
          to someone in English.
        </li>
        <li>
          <span className="font-semibold">Paris Tourist Office:</span> General
          tourist guidance. They may not resolve a transit payment issue
          directly, but can point you the right way.
        </li>
      </ul>
    </Card>
    <Card
      title="Contact Your Embassy"
      icon={<Building2 className="w-6 h-6 text-red-700" />}
    >
      <p className="mb-2">
        For a lost passport, theft of your documents, legal trouble, or a
        serious emergency abroad, your embassy or consulate in Paris is the
        place to call. Most English-speaking embassies have a 24-hour emergency
        line — find the current number on their official page:
      </p>
      <ul className="list-disc list-inside text-blue-700 dark:text-blue-400 space-y-1">
        <li>
          <a
            href="https://fr.usembassy.gov/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            United States
          </a>
        </li>
        <li>
          <a
            href="https://www.gov.uk/world/organisations/british-embassy-paris"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            United Kingdom
          </a>
        </li>
        <li>
          <a
            href="https://www.international.gc.ca/country-pays/france/paris.aspx"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Canada
          </a>
        </li>
        <li>
          <a
            href="https://france.embassy.gov.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Australia
          </a>
        </li>
        <li>
          <a
            href="https://www.ireland.ie/en/france/paris/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Ireland
          </a>
        </li>
      </ul>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Not listed? Search "[your country] embassy Paris" for the official site.
      </p>
    </Card>
  </div>
);

// --- Component: TroubleshootingTab ---
// Content for the Troubleshooting tab
const TroubleshootingTab = () => (
  <div>
    <h2 className="2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Troubleshooting</h2>
    <Card
      title="Rejected Credit Cards"
      icon={<XCircle className="w-6 h-6 text-red-700" />}
    >
      {" "}
      {/* Darker French Red icon */}
      <p className="mb-2">
        American (and some other non-European) credit cards can sometimes be
        problematic:
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Chip & PIN vs. Swipe/Signature:</span>{" "}
          European systems heavily rely on Chip & PIN. If your card is
          chip-enabled but requires a signature, it might be rejected by
          automated machines.
        </li>
        <li>
          <span className="font-semibold">Pre-authorization:</span> Some banks
          might flag international transactions as suspicious. Inform your bank
          before travel.
        </li>
        <li>
          <span className="font-semibold">Try another card:</span> If you have
          multiple cards (Visa, Mastercard, Amex), try a different one.
        </li>
        <li>
          <span className="font-semibold">Use a physical card:</span> Sometimes
          mobile payments (Apple Pay, Google Pay) linked to the same card work
          when the physical card doesn't, or vice-versa.
        </li>
        <li>
          <span className="font-semibold">Look for staffed counters:</span>{" "}
          Human agents can often process payments that machines reject.
        </li>
      </ul>
    </Card>

    <Card
      title="App Payment Failures (Île-de-France Mobilités)"
      icon={<AlertCircle className="w-6 h-6 text-red-700" />}
    >
      {" "}
      {/* Darker French Red icon */}
      <p className="mb-2">
        The official app can be finicky for international users:
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Regional Restrictions:</span> Some
          apps have regional restrictions on card acceptance.
        </li>
        <li>
          <span className="font-semibold">Account Creation:</span> Fill in all
          required fields correctly, even if they seem unusual for
          your country (e.g., specific postal code formats).
        </li>
        <li>
          <span className="font-semibold">"Mes Tickets Navigo" Extension:</span>{" "}
          You need this extension to load tickets onto your phone's NFC chip.
          Make sure it's installed and updated.
        </li>
        <li>
          <span className="font-semibold">NFC Issues:</span> Check that your phone's
          NFC is enabled and that you're tapping correctly.
        </li>
        <li>
          <span className="font-semibold">Alternative:</span> If the app fails,
          revert to physical methods like Navigo Easy or paper tickets from
          machines.
        </li>
      </ul>
    </Card>

    <Card
      title="Gates Won't Open When You Tap"
      icon={<Ticket className="w-6 h-6 text-red-700" />}
    >
      <p className="mb-2">
        You tap, the gate stays shut, and a light or message flashes. Common
        causes and fixes:
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Tap and wait:</span> Hold the card or
          phone flat on the purple reader for a full second — don't swipe it
          past. Wait for the green arrow before pushing through.
        </li>
        <li>
          <span className="font-semibold">"Billet non valable":</span> The
          ticket is used up, expired, or the wrong type (e.g. a Bus-Tram ticket
          at a metro gate). Load or buy the correct one.
        </li>
        <li>
          <span className="font-semibold">Two cards at once:</span> If your
          phone case holds several contactless cards, the reader may get
          confused. Take the right card out and tap it alone.
        </li>
        <li>
          <span className="font-semibold">Keep the same card:</span> If you
          entered by tapping a bank card or phone, exit and transfer by tapping
          the exact same one — mixing cards can read as no valid journey.
        </li>
        <li>
          <span className="font-semibold">Still stuck:</span> Use the wide gate
          at the staffed counter and show your ticket or app to the agent. Never
          tailgate through behind someone — that reads as fare evasion.
        </li>
      </ul>
    </Card>

    <Card
      title="Airport Trips Cost Extra (CDG & Orly)"
      icon={<Train className="w-6 h-6 text-red-700" />}
    >
      <p className="mb-2">
        The single biggest surprise for visitors: airport journeys are{" "}
        <span className="font-semibold">not</span> covered by a normal single
        ticket, and they're excluded from the daily price cap and most passes.
      </p>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Buy the airport ticket:</span> Trips
          to or from Charles de Gaulle (RER B) and Orly (Orlyval, RER, tram, or
          bus) need a dedicated, higher-priced airport ticket. A standard single
          ticket will not open the airport gates.
        </li>
        <li>
          <span className="font-semibold">Passes don't always cover it:</span> A
          Navigo weekly/monthly pass covering all zones works to the airports,
          but Liberté+ pay-as-you-go and the daily cap do not include them —
          you'll be charged the airport fare separately.
        </li>
        <li>
          <span className="font-semibold">Keep the ticket until you exit:</span>{" "}
          You need to tap out at the airport, so don't discard it mid-journey.
        </li>
      </ul>
    </Card>

    <Card
      title="Lost, Stolen, or Damaged Card"
      icon={<HelpCircle className="w-6 h-6 text-red-700" />}
    >
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Navigo Easy (anonymous):</span> It's
          not tied to your name, so a lost card and its remaining tickets can't
          be recovered or refunded. Treat it like cash.
        </li>
        <li>
          <span className="font-semibold">Navigo (with your name/photo):</span>{" "}
          A personal Navigo can be blocked and reissued with the balance
          restored. Report it at a staffed counter or via the Île-de-France
          Mobilités app.
        </li>
        <li>
          <span className="font-semibold">Left it on a train?</span> Lost
          property (<span className="italic">objets trouvés</span>) is handled by
          the RATP/SNCF lost-and-found service — file a report online rather than
          searching stations yourself.
        </li>
        <li>
          <span className="font-semibold">Demagnetized paper ticket:</span> If a
          paper ticket stops scanning, don't throw it out — a station agent can
          often read it or swap it.
        </li>
      </ul>
    </Card>

    <Card
      title="Charged Twice, or Charged With No Ticket"
      icon={<CreditCard className="w-6 h-6 text-red-700" />}
    >
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <span className="font-semibold">Pending vs. final charge:</span> A card
          payment that seems to fail often leaves a temporary
          "pre-authorization" hold that drops off in a few days without becoming
          a real charge. Check your statement before assuming you paid twice.
        </li>
        <li>
          <span className="font-semibold">Paid but no ticket loaded:</span> If
          money left your account but the app or machine didn't deliver the
          ticket, keep the receipt or transaction reference — that's what a refund
          claim needs.
        </li>
        <li>
          <span className="font-semibold">Where to claim:</span> Refunds for
          failed app or machine purchases go through Île-de-France Mobilités
          customer service, not your bank. Start there before disputing the
          charge.
        </li>
      </ul>
    </Card>

    <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50/60 dark:bg-red-900/20 p-4 text-sm text-gray-700 dark:text-gray-300">
      <span className="font-semibold">Still stuck, or something's gone wrong?</span>{" "}
      See{" "}
      <a href="#emergency" className="font-semibold text-blue-700 dark:text-blue-400 underline">
        Emergency Help
      </a>{" "}
      for helpline numbers (including English-speaking ones) and what to do if
      you're traveling without a valid ticket.
    </div>
  </div>
);

// Metro-roundel avatar for the assistant — a nod to the Paris "M" logo.
const AssistantAvatar = ({ className = "" }) => (
  <div
    className={`grid shrink-0 place-items-center rounded-full bg-[#0055A4] text-white shadow-md ring-2 ring-white ${className}`}
    aria-hidden="true"
  >
    <span className="font-display font-bold leading-none">M</span>
  </div>
);

// Starter prompts shown on the empty state so first-time tourists know what to ask.
const SUGGESTED_QUESTIONS = [
  "How do I get from CDG airport into central Paris?",
  "My contactless card was declined at the turnstile — what should I do?",
  "What's the cheapest option for 4 days of sightseeing?",
  "Can I just tap my phone to ride the metro?",
];

// --- Component: AssistantAnswer ---
// English answer is shown by default and is editable. French is opt-in: a
// collapsible panel that translates on demand (browser built-in translator, or
// server fallback) — most tourists just want English, and French is mainly a
// phrase to show an agent.
const AssistantAnswer = ({ english, french, onChange }) => {
  const [showFrench, setShowFrench] = useState(false);
  const [editing, setEditing] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(false);
  const debounceRef = useRef(null);

  const runTranslation = async (text) => {
    setTranslateError(false);
    setIsTranslating(true);
    try {
      const fr = await translateToFrench(text);
      onChange({ french: fr });
    } catch (err) {
      console.error("Translation failed:", err);
      setTranslateError(true);
    } finally {
      setIsTranslating(false);
    }
  };

  // Open the French panel and translate on first open (nothing is translated
  // for users who stay in English).
  const handleToggleFrench = () => {
    const next = !showFrench;
    setShowFrench(next);
    if (next && !french) runTranslation(english);
  };

  // Editing the English invalidates the French. Re-translate only if the panel
  // is open; otherwise clear it so the next open produces a fresh translation.
  const handleEnglishChange = (e) => {
    const value = e.target.value;
    onChange({ english: value, french: "" });

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (showFrench) {
      debounceRef.current = setTimeout(() => runTranslation(value), 700);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="overflow-hidden rounded-2xl border border-blue-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
      {/* Tricolore accent strip */}
      <div className="flex h-1">
        <div className="flex-1 bg-[#0055A4]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#EF4135]" />
      </div>

      {/* English — editable */}
      <div className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-900/40 px-2.5 py-1 text-xs font-semibold text-blue-800 dark:text-blue-300">
            🇬🇧 English
          </span>
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-900/30 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300 transition hover:bg-amber-100 dark:hover:bg-amber-900/50"
          >
            <Pencil className="h-3 w-3" />
            {editing ? "Done" : "Edit"}
          </button>
        </div>
        {editing ? (
          <textarea
            value={english}
            onChange={handleEnglishChange}
            autoFocus
            rows={Math.min(14, Math.max(4, Math.ceil((english.length || 1) / 48)))}
            className="w-full resize-y rounded-xl border border-transparent bg-blue-50/50 dark:bg-gray-900/50 p-3 font-mono text-[13px] leading-relaxed text-gray-800 dark:text-gray-100 transition focus:border-blue-300 focus:bg-white dark:focus:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
            aria-label="Assistant answer in English (editable)"
          />
        ) : (
          <div className="rounded-xl bg-blue-50/40 dark:bg-gray-900/40 p-3">
            <Markdown
              text={english}
              className="text-sm text-gray-800 dark:text-gray-100"
            />
          </div>
        )}

        {/* French — opt-in, translated on demand */}
        <div className="mt-3 border-t border-blue-50 dark:border-gray-700 pt-3">
          <button
            type="button"
            onClick={handleToggleFrench}
            onMouseEnter={warmUpTranslator}
            aria-expanded={showFrench}
            className="inline-flex items-center gap-2 rounded-full border border-red-100 dark:border-red-900/50 bg-red-50/60 dark:bg-red-900/20 px-3 py-1.5 text-sm font-medium text-[#c0392b] dark:text-red-300 transition hover:bg-red-50 dark:hover:bg-red-900/30"
          >
            <Languages className="h-4 w-4" />
            {showFrench ? "Hide French" : "Show in French"}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${showFrench ? "rotate-180" : ""}`}
            />
          </button>
          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
            to show a driver or agent
          </span>

          {showFrench && (
            <div className="mt-2 animate-msg-in rounded-xl border border-red-100 dark:border-gray-700 bg-[#FBFAF6] dark:bg-gray-900/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 dark:bg-red-900/30 px-2.5 py-1 text-xs font-semibold text-[#c0392b] dark:text-red-300">
                  🇫🇷 Français
                </span>
                {isTranslating ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#EF4135]" />
                    traduction…
                  </span>
                ) : (
                  translateError && (
                    <span className="text-[11px] text-gray-400">
                      traduction indisponible
                    </span>
                  )
                )}
              </div>
              <div className="min-h-[2.5rem] text-sm leading-relaxed text-gray-800 dark:text-gray-100">
                {isTranslating && !french ? (
                  <span className="italic text-gray-400">
                    Traduction en cours…
                  </span>
                ) : french ? (
                  <Markdown text={french} />
                ) : (
                  <span className="italic text-gray-400">
                    La traduction apparaîtra ici.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Component: TransitAssistantTab ---
// Bilingual assistant: Claude (server-side) returns the answer in English and
// French; edits to the English are re-translated to French in the browser.
const TransitAssistantTab = () => {
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const scrollRef = useRef(null);

  // Merge a patch into one assistant message by index (used for edits/translation).
  const updateMessage = (index, patch) => {
    setChatHistory((prev) =>
      prev.map((msg, i) => (i === index ? { ...msg, ...patch } : msg))
    );
  };

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory, isLoading]);

  // Accepts an optional preset (from a suggestion chip); otherwise uses the input.
  const handleAsk = async (preset) => {
    const userQuestion = (preset ?? question).trim();
    if (!userQuestion || isLoading) return;

    setIsLoading(true);
    setChatHistory((prev) => [...prev, { role: "user", text: userQuestion }]);
    setQuestion("");

    try {
      // Send only the raw question. The system prompt and current fares are
      // assembled server-side in the Netlify function so the key can't be abused
      // and the assistant always quotes live prices from fares.json.
      const response = await fetch(api.geminiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: userQuestion }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      const result = await response.json();
      const english = (result.text || result.english || "").trim();

      if (english) {
        // French is translated on demand (only if the user opens the panel),
        // so store it empty here.
        setChatHistory((prev) => [...prev, { role: "model", english, french: "" }]);
      } else {
        setChatHistory((prev) => [
          ...prev,
          { role: "model", english: prompts.errors.noAnswer, french: "" },
        ]);
      }
    } catch (error) {
      console.error("Error calling backend API:", error);
      setChatHistory((prev) => [
        ...prev,
        { role: "model", english: prompts.errors.fetchError, french: "" },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const canSend = question.trim().length > 0 && !isLoading;

  return (
    <div>
      {/* Welcome header — a friendly Parisian greeting */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0055A4] via-[#0a4a91] to-[#00337a] p-5 text-white shadow-lg sm:p-6">
        <div className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-14 left-8 h-36 w-36 rounded-full bg-[#EF4135]/25 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <AssistantAvatar className="h-14 w-14 text-2xl ring-white/40" />
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              Bonjour! I'm your Paris transit guide
            </h2>
            <p className="mt-1 max-w-xl text-sm text-blue-50/90">
              Ask anything about tickets, Navigo, or getting around — in
              English. I answer in both English and French, using today's
              official fares.
            </p>
          </div>
        </div>
      </div>

      {/* Editable hint */}
      <div className="mb-4 mt-3 flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
        <Pencil className="mt-0.5 h-4 w-4 shrink-0 text-blue-700 dark:text-blue-400" />
        <span>
          Edit the English reply and the French updates itself — handy to show a
          driver or agent exactly what you mean.
        </span>
      </div>

      <div className="flex flex-col space-y-4">
        {/* Conversation */}
        <div
          ref={scrollRef}
          className="chat-scroll max-h-[28rem] space-y-4 overflow-y-auto rounded-2xl border border-blue-100 dark:border-gray-700 bg-[#FBFAF6] dark:bg-gray-900 p-4 shadow-inner"
        >
          {chatHistory.length === 0 ? (
            <div className="flex flex-col items-center gap-4 px-2 py-8 text-center">
              <div className="text-4xl" aria-hidden="true">
                👋
              </div>
              <p className="max-w-md text-gray-600 dark:text-gray-400">
                New to the Paris metro? Tap a question to get started, or type
                your own below.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => handleAsk(q)}
                    onMouseEnter={warmUpTranslator}
                    className="rounded-full border border-blue-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-left text-sm text-blue-800 dark:text-blue-300 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-400 hover:bg-blue-50 dark:hover:border-blue-500 dark:hover:bg-gray-700 hover:shadow"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            chatHistory.map((msg, index) =>
              msg.role === "user" ? (
                <div key={index} className="flex animate-msg-in justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-br from-blue-600 to-blue-700 px-4 py-2.5 text-white shadow-sm">
                    {msg.text}
                  </div>
                </div>
              ) : (
                <div key={index} className="flex animate-msg-in gap-3">
                  <AssistantAvatar className="mt-1 hidden h-9 w-9 text-sm sm:grid" />
                  <div className="min-w-0 flex-1">
                    <AssistantAnswer
                      english={msg.english}
                      french={msg.french}
                      onChange={(patch) => updateMessage(index, patch)}
                    />
                  </div>
                </div>
              )
            )
          )}
          {isLoading && (
            <div className="flex animate-msg-in gap-3">
              <AssistantAvatar className="mt-1 hidden h-9 w-9 text-sm sm:grid" />
              <div className="inline-flex items-center gap-1.5 rounded-2xl rounded-tl-md border border-blue-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3.5 shadow-sm">
                <span
                  className="typing-dot h-2 w-2 rounded-full bg-blue-400"
                  style={{ animationDelay: "0ms" }}
                />
                <span
                  className="typing-dot h-2 w-2 rounded-full bg-blue-400"
                  style={{ animationDelay: "160ms" }}
                />
                <span
                  className="typing-dot h-2 w-2 rounded-full bg-blue-400"
                  style={{ animationDelay: "320ms" }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Input — pill with an embedded send button */}
        <div>
          <div className="relative">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onFocus={warmUpTranslator}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAsk();
              }}
              placeholder={defaults.placeholders.chatInput}
              className="w-full rounded-full border border-blue-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-3.5 pl-5 pr-14 text-gray-800 dark:text-gray-100 shadow-sm transition placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
              disabled={isLoading}
            />
            <button
              onClick={() => handleAsk()}
              aria-label="Send question"
              className="absolute right-1.5 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-[#0055A4] text-white shadow transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={!canSend}
            >
              {isLoading ? (
                <svg
                  className="h-5 w-5 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="mt-2 text-center text-xs text-gray-400">
            Powered by Claude · Live fares from Île-de-France Mobilités
          </p>
        </div>
      </div>
    </div>
  );
};

// Function to format the date as "Month Day, Year"
const getFormattedDate = () => {
  const today = new Date();
  const options = { year: "numeric", month: "long", day: "numeric" };
  return today.toLocaleDateString("en-US", options); // 'en-US' for English month names
};

// --- Main App Component ---
const App = () => {
  const [activeTab, setActiveTab] = useState("paymentMethods");
  // Start light for a deterministic first render (matches the prerendered HTML),
  // then adopt whatever the no-flash script in index.html already decided.
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const isDark = document.documentElement.classList.contains("dark");
    setTheme(isDark ? "dark" : "light");
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      const root = document.documentElement;
      root.classList.toggle("dark", next === "dark");
      try {
        localStorage.setItem("theme", next);
      } catch (e) {
        // Ignore storage errors (e.g. Safari private mode)
      }
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute("content", next === "dark" ? "#111827" : "#0055A4");
      return next;
    });
  };

  // Use scraped fares if available, fallback to defaults
  const [pricingData, setPricingData] = useState({
    singleTicket: scrapedFares.singleTicket || `\u20AC${defaults.pricing.singleTicket}`,
    singleTicketReduced: scrapedFares.singleTicketReduced || `\u20AC${defaults.pricing.singleTicketReduced}`,
    busTramTicket: scrapedFares.busTramTicket || `\u20AC${defaults.pricing.busTramTicket}`,
    busTramTicketReduced: scrapedFares.busTramTicketReduced || `\u20AC${defaults.pricing.busTramTicketReduced}`,
    navigoEasyCard: scrapedFares.navigoEasyCard || `\u20AC${defaults.pricing.navigoEasyCard}`,
    navigoDecouverte: scrapedFares.navigoDecouverte || `\u20AC${defaults.pricing.navigoDecouverte}`,
    navigoLibertePlus: scrapedFares.navigoLibertePlus || `\u20AC${defaults.pricing.navigoLibertePlus}`,
    navigoLiberteBus: `\u20AC${defaults.pricing.navigoLiberteBus}`,
    dailyCap: `\u20AC${defaults.pricing.dailyCap}`,
    weeklyPass: scrapedFares.weeklyPass || `\u20AC${defaults.pricing.weeklyPass}`,
    monthlyPass: scrapedFares.monthlyPass || `\u20AC${defaults.pricing.monthlyPass}`,
    lastUpdated: scrapedFares.lastUpdated || defaults.pricing.lastUpdated,
    confidence: scrapedFares.confidence || defaults.pricing.confidence,
    source: scrapedFares.source || 'defaults',
  });

  // Fares are imported at build time from fares.json
  // Run `npm run scrape-fares` before build to update prices
  useEffect(() => {
    setPricingData((prevData) => ({
      ...prevData,
      lastUpdated: getFormattedDate(),
    }));
  }, []);

  // Keep the active tab in sync with the URL hash (shareable links + Back button).
  useEffect(() => {
    const syncFromHash = () => setActiveTab(tabForHash(window.location.hash));
    syncFromHash(); // handle deep links on mount (runs after hydration, avoids mismatch)
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);

  const selectTab = (key) => {
    const nextHash = TAB_HASHES[key];
    if (window.location.hash.replace(/^#/, "") !== nextHash) {
      window.location.hash = nextHash; // adds history entry + fires hashchange -> updates state
    } else {
      setActiveTab(key);
    }
  };

  const renderConfidenceIcon = (level) => {
    switch (level) {
      case "High":
        return (
          <CheckCircle className="inline-block w-4 h-4 text-green-500 mr-1" />
        );
      case "Medium":
        return (
          <RefreshCw className="inline-block w-4 h-4 text-yellow-500 mr-1" />
        );
      case "Low":
        return <XCircle className="inline-block w-4 h-4 text-red-500 mr-1" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-red-50 dark:from-gray-900 dark:to-gray-950 flex items-center justify-center p-4 font-sans transition-colors duration-300">
      {" "}
      {/* Soft French Blue and Red gradient */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 md:p-8 w-full max-w-3xl border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {theme === "dark" ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
        </button>
        <h1 className="text-3xl md:text-4xl font-extrabold text-center text-blue-800 dark:text-blue-300 mb-6">
          {" "}
          {/* French Blue heading */}
          Paris Transit Helper 🇫🇷
        </h1>
        <p className="text-center text-gray-600 dark:text-gray-300 -mt-4 mb-6 max-w-xl mx-auto">
          Paris metro tickets, Navigo, and fares explained in plain English,
          with live prices and a free AI assistant for tourists.
        </p>

        {/* Navigation Tabs */}
        <div
          role="tablist"
          aria-label="Guide sections"
          className="sticky top-2 z-20 flex justify-center gap-1 mb-8 bg-gray-50 dark:bg-gray-900 rounded-lg p-2 shadow-inner"
        >
          <TabButton
            icon={<CreditCard className="w-5 h-5" />}
            label="Payment Methods"
            isActive={activeTab === "paymentMethods"}
            onClick={() => selectTab("paymentMethods")}
          />
          <TabButton
            icon={<AlertCircle className="w-5 h-5" />}
            label="Emergency Help"
            isActive={activeTab === "emergencyHelp"}
            onClick={() => selectTab("emergencyHelp")}
          />
          <TabButton
            icon={<HelpCircle className="w-5 h-5" />}
            label="Troubleshooting"
            isActive={activeTab === "troubleshooting"}
            onClick={() => selectTab("troubleshooting")}
          />
          <TabButton
            icon={<MessageSquare className="w-5 h-5" />}
            label="AI Transit Assistant"
            isActive={activeTab === "transitAssistant"}
            onClick={() => selectTab("transitAssistant")}
          />
        </div>

        {/* Content Area */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-100 dark:border-gray-700 shadow-md">
          {activeTab === "paymentMethods" && (
            <PaymentMethodsTab pricingData={pricingData} />
          )}
          {activeTab === "emergencyHelp" && <EmergencyHelpTab />}
          {activeTab === "troubleshooting" && <TroubleshootingTab />}
          {activeTab === "transitAssistant" && <TransitAssistantTab />}
        </div>

        {/* Footer with data freshness and Buy Me a Coffee link */}
        <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
          <p className="flex items-center justify-center">
            Information last updated:{" "}
            <span className="font-semibold ml-1">
              {pricingData.lastUpdated}
            </span>
            <span className="ml-2 flex items-center">
              Confidence: {renderConfidenceIcon(pricingData.confidence)}
              <span className="font-semibold">{pricingData.confidence}</span>
            </span>
          </p>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Prices update automatically each day from the official published
            fares and are subject to change.
          </p>
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Please refer to this official website for the most accurate and
            up-to-date information:{" "}
            <a
              href={api.officialFaresUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
            >
              {api.officialFaresUrl}
            </a>
          </p>
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Made with ❤️ by{" "}
            <a
              href={api.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
            >
              Swapnil Tamse
            </a>
          </p>
          {/* Buy Me a Coffee Link */}
          {/* <p className="mt-4">
            <a
              href="https://www.buymeacoffee.com/swapniltamse" // REPLACE WITH YOUR ACTUAL BUY ME A COFFEE LINK
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition duration-300 ease-in-out shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 font-semibold" // French Red Buy Me a Coffee button
            >
              ☕ Buy Me a Coffee
            </a>
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default App;
